import * as THREE from 'three';
import { STOCKER_RACK_LEVELS } from '../../../../utils/stockerCapacity';
import type { AmhsDetailLevel } from '../rendering';
import type { LayoutStockerPortRef } from './StockerLoadPortBank';

/** Premium FAB stocker proportions (configurable via props). */
export const FAB_STOCKER_DEFAULTS = {
  width: 5.5,
  depth: 2.75,
  height: 7.5,
  /** Reference stocker — 7 shelf tiers per column. */
  levels: 7,
  loadPortCount: 3,
} as const;

export type StockerStatus = 'ready' | 'running' | 'warning' | 'error' | 'offline';

export type StockerRobotStatus =
  | 'idle'
  | 'movingToLoadPort'
  | 'picking'
  | 'movingToSlot'
  | 'placing';

export type StockerRobotState = {
  status: StockerRobotStatus;
  targetLoadPort?: number;
  targetSlot?: number;
  /** Normalized vertical carriage position 0–1 along rail. */
  carriageT?: number;
  /** Absolute carriage Y — overrides carriageT when set (transfer-driven). */
  carriageY?: number;
  /** Horizontal carriage offset (slot column travel). */
  carriageX?: number;
  /** Fork tray Z offset from the aisle center (local to StockerRobot). */
  forkLocalZ?: number;
  /** Normalized fork extension 0–1. */
  armExtendT?: number;
  /** Which axis the fork extends on during transfer motion. */
  forkMode?: 'port' | 'shelf' | 'retracted';
  /** FOUP visible on fork (transfer anim owns rack/port FOUP meshes). */
  carryingFoup?: boolean;
};

export type StockerFoupSlotState = {
  slotId: number;
  level: number;
  position: [number, number, number];
  occupied: boolean;
  highlighted?: boolean;
  status?: 'raw' | 'processed' | 'empty';
};

export type StockerLoadPortState = {
  portIndex: number;
  occupied: boolean;
  active?: boolean;
};

export type StockerAmhsEvent =
  | 'OHT_ARRIVED'
  | 'FOUP_DROPPED'
  | 'LOAD_PORT_OCCUPIED'
  | 'STOCKER_PICK_START'
  | 'FOUP_PICKED'
  | 'STOCKER_MOVE'
  | 'FOUP_PLACED'
  | 'STOCKER_OPERATION_COMPLETE';

export type Stocker3DProps = {
  id?: string;
  /** Layout / sim display name shown on the stocker cap. */
  label?: string;
  width?: number;
  depth?: number;
  height?: number;
  levels?: number;
  loadPortCount?: number;
  occupiedCount?: number;
  status?: StockerStatus;
  detail?: AmhsDetailLevel;
  autoDetail?: boolean;
  opacity?: number;
  animate?: boolean;
  position?: [number, number, number];
  rotation?: [number, number, number];
  onSelect?: () => void;
  bayCount?: string | number;
  outsideStocker?: boolean;
  layoutPorts?: LayoutStockerPortRef[];
  robotState?: StockerRobotState;
  loadPorts?: StockerLoadPortState[];
  slots?: StockerFoupSlotState[];
  /** Height above cabinet top for OHT hover / drop alignment. */
  ohtDropHeight?: number;
  onFoupReceived?: (slotId: number) => void;
  onFoupPicked?: (slotId: number) => void;
  onFoupStored?: (slotId: number) => void;
  onFoupRetrieved?: (slotId: number) => void;
  onFoupPlaced?: (portIndex: number) => void;
  onAmhsEvent?: (event: StockerAmhsEvent, payload?: Record<string, unknown>) => void;
};

export type StockerDimensions = {
  width: number;
  depth: number;
  height: number;
  baseH: number;
  capH: number;
  pillarBottom: number;
  pillarTop: number;
  /** Bottom-front bay height — first-shelf load-port band. */
  loadPortBayH: number;
  /** Y at top of first shelf tier / load-port lintel. */
  loadPortTopY: number;
  /** FOUP deck surface Y — aligned with first shelf tray (OHT handoff). */
  loadPortDeckY: number;
  /** Bottom Y of rack slot volumes (level 0). */
  rackBottom: number;
  /** Top Y of rack slot volumes. */
  rackTop: number;
  /** First shelf tier height (slot volume). */
  slotH: number;
  /** Center Z of external load-port deck (−Z = front, outside frame). */
  loadPortZ: number;
  /** Outermost −Z edge of the extended OHT load deck. */
  loadPortDeckFrontZ: number;
  /** Front pillar face (−Z). */
  frontFaceZ: number;
  /** Internal lifter aisle Z — behind load port, inside open front bay. */
  lifterAisleZ: number;
  /** Lifter mast X — aligned with primary load-port / FOUP center. */
  lifterX: number;
  /** FOUP seat height on external load-port pad. */
  foupSeatY: number;
  glassH: number;
  glassY: number;
  glassW: number;
  frontZ: number;
  frameT: number;
};

/** Rack vertical metrics shared by dimensions, layout, and load-port alignment. */
export function computeStockerRackVerticals(
  height: number,
  pillarBottom: number,
  pillarTop: number,
  frameT: number
) {
  const rackBottom = pillarBottom + frameT * 1.05;
  const rackTop = pillarTop - frameT * 0.95;
  const usableH = Math.max(0.5, rackTop - rackBottom - height * 0.01);
  const gapY = usableH * 0.14;
  const slotH = (usableH - gapY * (STOCKER_RACK_LEVELS - 1)) / STOCKER_RACK_LEVELS;
  const trayH = slotH * 0.12;
  const firstShelfDeckY = rackBottom + trayH;
  return { rackBottom, rackTop, gapY, slotH, trayH, firstShelfDeckY };
}

/** Shared vertical zones — pillars, glass, and caps use the same bounds (no gaps). */
export function resolveStockerDimensions(
  width: number,
  height: number,
  depth: number
): StockerDimensions {
  /** Thin floor plinth — first shelf starts immediately above. */
  const baseH = Math.max(height * 0.018, 0.06);
  const capH = height * 0.085;
  const pillarBottom = baseH;
  const pillarTop = height - capH;
  const frameT = width * 0.026;
  const rack = computeStockerRackVerticals(height, pillarBottom, pillarTop, frameT);
  const loadPortDeckY = rack.firstShelfDeckY;
  const loadPortTopY = rack.rackBottom + rack.slotH;
  const loadPortBayH = loadPortTopY - pillarBottom;
  const glassH = pillarTop - rack.rackBottom - frameT * 0.6;
  const glassY = rack.rackBottom + glassH * 0.5;
  const glassW = width * 0.78;
  /** Open front / OHT face is −Z (swapped from previous +Z). */
  const frontFaceZ = -depth * 0.46;
  /** How far load-port deck protrudes past the glass frame toward OHT (−Z). */
  const loadPortProtrude = depth * 0.16;
  const loadPortZ = frontFaceZ - loadPortProtrude;
  /** Front edge of the external OHT drop deck (−Z, outside the cabinet). */
  const loadPortDeckFrontZ = loadPortZ - depth * 0.08;
  const lifterAisleZ = frontFaceZ + depth * 0.08;
  const lifterX = 0;
  const foupSeatY = loadPortDeckY + height * 0.018;
  const frontZ = frontFaceZ;
  return {
    width,
    depth,
    height,
    baseH,
    capH,
    pillarBottom,
    pillarTop,
    loadPortBayH,
    loadPortTopY,
    loadPortDeckY,
    rackBottom: rack.rackBottom,
    rackTop: rack.rackTop,
    slotH: rack.slotH,
    loadPortZ,
    loadPortDeckFrontZ,
    frontFaceZ,
    lifterAisleZ,
    lifterX,
    foupSeatY,
    glassH,
    glassY,
    glassW,
    frontZ,
    frameT,
  };
}

/** FOUP world pose when resting on the external load-port pad. */
export function resolveLoadPortFoupPose(
  dims: StockerDimensions,
  portX: number,
  portZ: number
): [number, number, number] {
  return [portX, dims.foupSeatY, portZ];
}

/** @deprecated Use buildStockerRackLayout + rackLayoutToFoupSlotStates. */
export function computeStockerSlotLayout(
  width: number,
  height: number,
  depth: number,
  levels: number
): StockerFoupSlotState[] {
  const baseY = height * 0.14;
  const topY = height * 0.82;
  const levelGap = (topY - baseY) / Math.max(1, levels - 1);
  const slotX = width * 0.29;
  const slotZ = depth * 0.03;
  const out: StockerFoupSlotState[] = [];
  let slotId = 0;
  for (let level = 0; level < levels; level += 1) {
    for (const side of [-1, 1] as const) {
      out.push({
        slotId: slotId++,
        level,
        position: [side * slotX, baseY + level * levelGap, slotZ],
        occupied: false,
      });
    }
  }
  return out;
}

export function computeFrontLoadPortPositions(
  width: number,
  height: number,
  depth: number,
  count: number
): [number, number, number][] {
  const dims = resolveStockerDimensions(width, height, depth);
  const n = Math.max(1, Math.min(4, count));
  const y = dims.loadPortDeckY;
  const z = dims.loadPortZ;
  const span = width * 0.62;
  if (n === 1) return [[0, y, z]];
  return Array.from({ length: n }, (_, i) => {
    const t = n <= 1 ? 0 : (i / (n - 1)) * 2 - 1;
    return [t * span * 0.5, y, z] as [number, number, number];
  });
}

/** World-space OHT drop anchor above a load port (local coords — apply stocker transform). */
export function resolveOhtDropPosition(
  loadPortLocal: [number, number, number],
  stockerHeight: number,
  dropHeight = 1.2
): THREE.Vector3 {
  return new THREE.Vector3(loadPortLocal[0], stockerHeight + dropHeight, loadPortLocal[2]);
}
